import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);

  const verifyPayment = useCallback(async (sessionId) => {
    // Poll subscription status to verify payment
    let attempts = 0;
    const maxAttempts = 5;
    
    const poll = async () => {
      try {
        const response = await axios.get(`${API}/api/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.subscription_status === 'active') {
          setSuccess(true);
          setVerifying(false);
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setVerifying(false);
          setSuccess(true); // Assume success after max attempts
        }
      } catch (error) {
        console.error('Error verifying payment:', error);
        setVerifying(false);
      }
    };
    
    poll();
  }, [token]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      verifyPayment(sessionId);
    } else {
      setVerifying(false);
    }
  }, [searchParams, verifyPayment]);

  return (
    <div className="page-container p-4 md:p-8 noise-overlay min-h-screen flex items-center justify-center">
      <div className="relative z-10 max-w-md w-full">
        <Card className="glass-card border-border/50">
          <CardHeader className="text-center">
            {verifying ? (
              <>
                <div className="flex justify-center mb-4">
                  <Loader2 className="w-16 h-16 text-blue-500 animate-spin" />
                </div>
                <CardTitle className="text-2xl">Vérification du paiement...</CardTitle>
                <CardDescription>
                  Veuillez patienter pendant que nous confirmons votre abonnement.
                </CardDescription>
              </>
            ) : (
              <>
                <div className="flex justify-center mb-4">
                  <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-12 h-12 text-green-500" />
                  </div>
                </div>
                <CardTitle className="text-2xl text-green-500">Paiement réussi !</CardTitle>
                <CardDescription>
                  Merci pour votre abonnement à DevisFast Pro.
                </CardDescription>
              </>
            )}
          </CardHeader>

          {!verifying && (
            <CardContent className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4 text-center">
                <p className="text-sm text-muted-foreground">
                  Votre abonnement est maintenant actif. Vous avez accès à toutes les fonctionnalités de DevisFast.
                </p>
              </div>

              <Button 
                onClick={() => navigate('/dashboard')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                size="lg"
              >
                Accéder au tableau de bord
              </Button>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;
